/**
 * GlobalTutorialBar - Persistent tutorial control bar
 * Renders across all pages and follows navigation
 */

import React from 'react';
import { Coachmark } from './Coachmark';
import { HelpMenu } from './HelpMenu';
import { TutorialProgressBar } from './TutorialProgressBar';
import { useTutorial } from '@/contexts/TutorialContext';
import { FirstUseOnboardingCoordinator } from './FirstUseOnboardingCoordinator';
import { TutorialCompletionNotice } from './TutorialCompletionNotice';
// CORE-BL: progress is measured against the filtered sequence.
import { useNavigationStepDetector } from '@/hooks/useNavigationStepDetector';
import { useFormFactor } from '@/hooks/use-mobile';

export const GlobalTutorialBar = () => {
  const {
    isActive,
    flow,
    isMinimized,
    currentStep,
    completed,
    isHelpMode,
    replayTutorial,
    activeSteps,
    experienceLabel,
    helpButtonHidden,
    hideHelpButton,
} = useTutorial();
  const formFactor = useFormFactor();
  const isCompactMode = formFactor === 'phone';
  
  // Detect clicks on navigation elements (profile button, hours tab)
  useNavigationStepDetector();

  // Add dynamic bottom padding when tutorial is active AND not minimized
  React.useEffect(() => {
    if (isActive && !isMinimized) {
      document.body.classList.add('tutorial-active');
      if (isCompactMode) {
        document.body.classList.add('tutorial-active-mobile');
      }
    } else {
      document.body.classList.remove('tutorial-active', 'tutorial-active-mobile');
    }
    
    return () => {
      document.body.classList.remove('tutorial-active', 'tutorial-active-mobile');
    };
  }, [isActive, isMinimized, isCompactMode]);

  const steps = activeSteps;

  // First-use delivery is rendered BEFORE the early return below, and that
  // ordering is the whole point. The bar has nothing to show to somebody who
  // has not started a tutorial — which is exactly who the invitation is for.
  // Mounting the coordinator inside the "there is a tutorial" branch would
  // have reproduced the Dashboard problem in a new place.
  const firstUse = (
    <>
      <FirstUseOnboardingCoordinator />
      {/* Mounted OUTSIDE the `isActive` condition below, and outside the
          early return, deliberately. Completing a walkthrough sets
          `completed`, which makes `isActive` false — so the coachmark that
          raised the notice is unmounted by this very component at the moment
          the notice is needed. Owning the notice in the context and rendering
          it here is the only placement that survives that. */}
      <TutorialCompletionNotice />
    </>
  );

  if (!flow && !isHelpMode) return firstUse;

  return (
    <>
      {firstUse}

      {/* Tutorial progress bar */}
      <TutorialProgressBar
        currentStep={currentStep}
        totalSteps={steps.length}
        isActive={isActive}
        isCompleted={completed}
      />

      {/* Dynamic padding style injection */}
      <style>{`
        body.tutorial-active {
          padding-bottom: 120px;
        }
        
        body.tutorial-active-mobile {
          padding-bottom: 90px;
        }
        
        @media (min-width: 640px) {
          body.tutorial-active {
            padding-bottom: 112px;
          }
          
          body.tutorial-active-mobile {
            padding-bottom: 112px;
          }
        }
      `}</style>
      
      {/* Active tutorial coachmarks */}
      {isActive && steps.map((step) => (
        <Coachmark
          key={step.id}
          stepId={step.id}
          targetSelector={step.coachmarks[0]}
          arrowPosition="bottom"
          arrowOffset={20}
        />
      ))}

      {/* Help mode — the floating button after completion.
          `helpButtonHidden` is consumed HERE. It used to be `closeHelpMenu`
          setting `showHelpMenu`, a value nothing rendered from: the button
          labelled "Hide Help Button" could not hide itself, because
          visibility came from `isHelpMode` alone. */}
      {isHelpMode && !helpButtonHidden && (
        <HelpMenu
          // Replays THIS person's experience. It used to hand the menu a
          // choice between `manager` and `user`, which is how a completed
          // technician was offered two walkthroughs that were not theirs.
          onReplay={() => {
            void replayTutorial();
          }}
          // Hides the affordance for this session only. It does NOT dismiss
          // the walkthrough: `completed` stays true, `dismissed` stays false,
          // and the account menu still offers it.
          onDismiss={hideHelpButton}
          experienceLabel={experienceLabel}
        />
      )}
    </>
  );
};
